import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { findByEventoCodigo, getEngajamentoInfo } from "./api-evento-usuario";
import TablePaginationActions from "../../../shared/components/Table/Actions";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { dateTimeFormat } from "../../../util/DateUtil";
import Loading from "../../UI/Loading";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const toNumber = (value) => (typeof value === "number" ? value : 0);
const percent = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

const RESPOSTA_COR = { Sim: "success", Não: "error", Nao: "error", Talvez: "warning" };

const StatCard = ({ label, value, total }) => (
  <Grid size={{ xs: 6, sm: 4, md: 12 / 7 }}>
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600}>
        {value}
      </Typography>
      {total !== undefined && (
        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <LinearProgress
            variant="determinate"
            value={percent(value, total)}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            {percent(value, total)}%
          </Typography>
        </Stack>
      )}
    </Paper>
  </Grid>
);

export default function EngajamentoTable({ evento }) {
  const [eventoUsuarios, setEventoUsuarios] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [engajamento, setEngajamento] = useState(null);

  useEffect(() => {
    setPageNumber(0);
    let active = true;
    getEngajamentoInfo(evento.codigo).then((data) => {
      if (!active || !data) return;
      if (data.error) {
        errorMessage(data.message);
      } else {
        setEngajamento(data);
      }
    });
    return () => {
      active = false;
    };
  }, [evento.codigo]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByEventoCodigo(evento.codigo, pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setEventoUsuarios({ ...EMPTY_PAGE, ...data });
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [evento.codigo, pageNumber]);

  const total = toNumber(engajamento?.total);

  const renderTable = () => {
    if (isLoading) {
      return (
        <Box py={4}>
          <Loading description="Carregando engajamento..." />
        </Box>
      );
    }
    if (eventoUsuarios.content.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          Nenhum destinatário encontrado.
        </Typography>
      );
    }
    return (
      <>
        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 650 }} aria-label="Engajamento do evento">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                <TableCell>Nome</TableCell>
                <TableCell>Perfil</TableCell>
                <TableCell align="center">Visualizado</TableCell>
                <TableCell>Resposta</TableCell>
                <TableCell>Enviado em</TableCell>
                <TableCell>Última atualização</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eventoUsuarios.content.map((eu) => (
                <TableRow key={eu.codigo} hover>
                  <TableCell>{eu.usuario?.nome}</TableCell>
                  <TableCell>{eu.usuario?.perfil}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={eu.visualizado ? "Visualizado" : "Não visualizado"}>
                      {eu.visualizado ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {eu.resposta ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={eu.resposta}
                        color={RESPOSTA_COR[eu.resposta] ?? "default"}
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Sem resposta
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{eu.createdAt ? dateTimeFormat(eu.createdAt) : ""}</TableCell>
                  <TableCell>{eu.lastModified ? dateTimeFormat(eu.lastModified) : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          rowsPerPage={eventoUsuarios.size || 0}
          component="div"
          count={eventoUsuarios.totalElements || 0}
          page={eventoUsuarios.number || 0}
          onPageChange={(event, newPage) => setPageNumber(newPage)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? `${count} registros` : `mais de ${to}`}`
          }
          ActionsComponent={TablePaginationActions}
        />
      </>
    );
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Engajamento
      </Typography>

      {engajamento && (
        <Grid container spacing={2}>
          <StatCard label="Total enviado" value={total} />
          <StatCard label="Visualizado" value={toNumber(engajamento.visualizado)} total={total} />
          <StatCard label="Não visualizado" value={toNumber(engajamento.naoVisualizado)} total={total} />
          <StatCard label="Sim" value={toNumber(engajamento.sim)} total={total} />
          <StatCard label="Não" value={toNumber(engajamento.nao)} total={total} />
          <StatCard label="Talvez" value={toNumber(engajamento.talvez)} total={total} />
          <StatCard label="Sem resposta" value={toNumber(engajamento.semResposta)} total={total} />
        </Grid>
      )}

      {renderTable()}
    </Stack>
  );
}
